import { Text } from '@mantine/core';
import type { InboxMessage } from '../types';
import {
  AttachmentCard,
  BubbleText,
  MediaBubble,
  QuickReplyChips,
  SoftNote,
  TemplateButtons,
  TemplateCards,
  unsupportedNote,
  type BubbleButton,
  type BubbleCard,
  type MediaKind,
} from '../bubbles';
import type { BubbleBodyProps, BubbleBtn, BubbleCardData, ReceiptData } from '../MetaBubbles';
import {
  buttonLink,
  buttonTemplate,
  firstAttachment,
  genericElements,
  parseMetaPayload,
  quickReplyTitles,
  receiptPayload,
  stickerIdOf,
  type MetaButton,
  type MetaElement,
} from '../../../lib/meta-message-payload';

/**
 * Messenger message rendering.
 *
 * The wire-format reading lives in `lib/meta-message-payload.ts` — Instagram
 * Direct rides the identical Messenger Platform envelope, so both channels'
 * renderers used to carry their own copy of the same `JSON.parse` helpers.
 */

function toBubbleButton(b: MetaButton): BubbleButton {
  return { title: b.title ?? '', url: buttonLink(b) };
}

const MEDIA_KINDS = new Set<MediaKind>(['image', 'video', 'audio', 'file']);

function toBubbleCard(el: MetaElement): BubbleCard {
  return {
    title: el.title ?? null,
    subtitle: el.subtitle ?? null,
    imageUrl: el.image_url ?? null,
    buttons: (el.buttons ?? []).map(toBubbleButton),
  };
}

/** Renders one Messenger message: media, quick-reply chips, button/carousel
 *  template previews, reactions, or plain text. Delegates the visuals to the
 *  shared bubble components so Messenger, Instagram and the builder preview
 *  stay identical. */
export function renderMessengerContent(m: InboxMessage) {
  if (m.messageType === 'reaction') {
    return <Text size="lg">{m.text || '👍'}</Text>;
  }

  const p = parseMetaPayload(m.payloadJson);

  if (m.messageType === 'quick_replies') {
    return <QuickReplyChips text={m.text} chips={quickReplyTitles(p)} />;
  }

  if (m.messageType === 'generic_template') {
    return <TemplateCards cards={genericElements(p).map(toBubbleCard)} />;
  }

  if (m.messageType === 'button_template') {
    const b = buttonTemplate(p);
    return (
      <>
        {(b?.text || m.text) && <BubbleText>{b?.text ?? m.text}</BubbleText>}
        <TemplateButtons buttons={(b?.buttons ?? []).map(toBubbleButton)} />
      </>
    );
  }

  const att = firstAttachment(p);
  if (att?.type && MEDIA_KINDS.has(att.type as MediaKind)) {
    const url = m.mediaUrl ?? att.payload?.url ?? null;
    return <MediaBubble kind={att.type as MediaKind} url={url} alt="Shared media" />;
  }
  if (att?.type) {
    // Non-media attachment we don't have a dedicated renderer for.
    return (
      <AttachmentCard label="Shared attachment" url={m.mediaUrl ?? att.payload?.url ?? null} />
    );
  }

  if (m.text) return <BubbleText>{m.text}</BubbleText>;
  return <SoftNote app="Messenger" />;
}

/** Map a `web_url`/`phone_number`/`audio_call`/postback button onto the native
 *  bubble's button kind (drives the leading icon). */
function metaBtn(b: MetaButton): BubbleBtn {
  const kind: BubbleBtn['kind'] =
    b.type === 'web_url'
      ? 'url'
      : b.type === 'phone_number' || b.type === 'audio_call'
        ? 'phone'
        : 'reply';
  return { title: b.title ?? '', kind };
}

/**
 * Native-Meta bubble mapper: turns a normalized Messenger message into typed
 * `MetaBubbles` body props (the per-type layouts — buttons/cards/receipt/etc.)
 * instead of JSX. Mirrors `renderMessengerContent`'s parsing; the inbox shell
 * prefers this for the native look.
 */
export function messengerToBubbleProps(m: InboxMessage): BubbleBodyProps {
  if (m.messageType === 'reaction') {
    return { type: 'text', text: m.text || '👍' };
  }

  const p = parseMetaPayload(m.payloadJson);

  if (m.messageType === 'quick_replies') {
    return { type: 'quickReplies', text: m.text ?? undefined, quickReplies: quickReplyTitles(p) };
  }
  if (m.messageType === 'generic_template') {
    const cards: BubbleCardData[] = genericElements(p).map((el) => ({
      imageUrl: el.image_url ?? undefined,
      title: el.title ?? undefined,
      subtitle: el.subtitle ?? undefined,
      buttons: (el.buttons ?? []).map(metaBtn),
    }));
    return { type: 'cards', cards };
  }
  if (m.messageType === 'button_template') {
    const b = buttonTemplate(p);
    return {
      type: 'buttons',
      text: b?.text ?? m.text ?? undefined,
      buttons: (b?.buttons ?? []).map(metaBtn),
    };
  }
  if (m.messageType === 'receipt_template') {
    const r = receiptPayload(p);
    const receipt: ReceiptData = {
      orderNumber: r?.order_number,
      name: r?.recipient_name,
      currency: r?.currency,
      items: (r?.elements ?? []).map((e) => ({
        title: e.title ?? 'Item',
        qty: e.quantity,
        price: e.price ?? 0,
      })),
      total: r?.summary?.total_cost,
      paid: r?.payment_method,
    };
    return { type: 'receipt', receipt };
  }

  const att = firstAttachment(p);
  // Sticker (incl. the 👍 like): Meta delivers it as an "image" attachment with
  // a sticker_id. Prefer the dedicated column; fall back to the payload for rows
  // ingested before classification existed.
  const stickerId = m.stickerId ?? stickerIdOf(att);
  if (m.messageType === 'sticker' || stickerId != null) {
    return { type: 'sticker', media: { url: m.mediaUrl ?? att?.payload?.url ?? undefined } };
  }
  const lat = m.latitude ?? att?.payload?.coordinates?.lat;
  const long = m.longitude ?? att?.payload?.coordinates?.long;
  if (m.messageType === 'location' || att?.type === 'location' || (lat != null && long != null)) {
    return {
      type: 'location',
      media: {
        lat: lat ?? undefined,
        long: long ?? undefined,
        url: att?.payload?.url ?? undefined,
      },
    };
  }
  if (m.messageType === 'fallback' || att?.type === 'fallback') {
    return { type: 'attachment', attachment: { label: att?.payload?.title ?? 'Shared link' } };
  }
  if (att?.type && MEDIA_KINDS.has(att.type as MediaKind)) {
    return {
      type: att.type as MediaKind,
      media: {
        url: m.mediaUrl ?? att.payload?.url ?? undefined,
        mimeType: m.mimeType ?? undefined,
      },
    };
  }
  if (att?.type) {
    return {
      type: 'attachment',
      attachment: {
        label: 'Shared attachment',
        imageUrl: m.mediaUrl ?? att.payload?.url ?? undefined,
      },
    };
  }
  if (m.text) return { type: 'text', text: m.text };
  return { type: 'text', text: unsupportedNote('Messenger') };
}
